import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { Button } from '../ui/button';
import HeroSlideshow from '../HeroSlideshow';
import { RevealText } from '../motion/Reveal';
import { useMotionMode } from '../../contexts/MotionContext';

const SLIDES = [
  { src: '/hero.jpg', alt: 'Obsidian Chauffeurs fleet — Mercedes V-Class', pan: 'in' },
  { src: '/hero/hero-2.jpg', alt: 'Obsidian Chauffeurs fleet at night — Manchester', pan: 'in' },
  { src: '/hero/hero-3.jpg', alt: 'Luxury V-Class interior — blue starlight ceiling', pan: 'out' },
  { src: '/hero/hero-4.jpg', alt: 'V-Class cabin from the rear doors — starlight headliner', pan: 'out' },
];

const fade = (delay) => ({ initial: { opacity: 0, y: 30 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.9, delay, ease: [0.16, 1, 0.3, 1] } });

export const HomeHero = ({ introDelay = 0 }) => {
  const ref = useRef(null);
  const { cinematic, reduced } = useMotionMode();
  const base = introDelay;
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });
  const k = reduced ? 0 : 1;
  const bgY = useTransform(scrollYProgress, [0, 1], ['0%', `${(cinematic ? 35 : 15) * k}%`]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1, 1 + (cinematic ? 0.25 : 0.08) * k]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, (cinematic ? -220 : -80) * k]);
  const textScale = useTransform(scrollYProgress, [0, 1], [1, cinematic ? 0.88 : 1]);
  const textOpacity = useTransform(scrollYProgress, (v) => (reduced ? 1 : 1 - Math.min(v / 0.65, 1)));
  const eyebrowY = useTransform(scrollYProgress, [0, 1], [0, (cinematic ? -380 : -120) * k]);
  const veil = useTransform(scrollYProgress, (v) => v * (cinematic ? 0.85 : 0.5));

  return (
    <section ref={ref} className="relative h-screen w-full overflow-hidden bg-black text-white" data-testid="home-hero">
      <motion.div className="absolute inset-0 will-change-transform" style={{ y: bgY, scale: bgScale }}>
        <HeroSlideshow slides={SLIDES} intervalMs={13000} fadeMs={2400} />
      </motion.div>
      <motion.div className="absolute inset-0 bg-black pointer-events-none" style={{ opacity: veil }} />
      <div className="absolute inset-0 oc-grain pointer-events-none" />

      <motion.div className="absolute top-32 lg:top-36 left-6 right-6 lg:left-16 z-10 flex items-center gap-3" style={{ y: eyebrowY }} {...fade(base + 0.1)}>
        <span className="h-px w-10 shrink-0 bg-white/60" />
        <span className="text-xs tracking-[0.3em] uppercase text-white/70">Executive Chauffeur Service</span>
      </motion.div>

      <motion.div
        className="relative z-10 h-full max-w-7xl mx-auto px-6 lg:px-16 flex flex-col justify-end pb-24 lg:pb-32 origin-bottom-left"
        style={{ y: textY, scale: textScale, opacity: textOpacity }}
      >
        <h1 className="text-5xl md:text-6xl lg:text-[5.5rem] font-bold leading-[0.95] tracking-tight max-w-4xl" data-testid="hero-heading">
          <RevealText text="The Ultimate" animateOnMount delay={base + 0.2} className="block" />
          <RevealText text="Travel Experience." animateOnMount delay={base + 0.4} className="block text-white/70 italic font-light" />
        </h1>
        <motion.p className="text-base md:text-lg text-white/75 mt-8 max-w-xl leading-relaxed" {...fade(base + 0.6)}>
          Manchester-based chauffeur service across the UK, defined by immaculate vehicles, discreet chauffeurs, and punctuality delivered with precision.
        </motion.p>
        <motion.div className="mt-10 flex flex-col sm:flex-row gap-3 sm:gap-4" {...fade(base + 0.75)}>
          <Link to="/booking">
            <Button size="lg" className="bg-white text-black hover:bg-gray-200 text-base px-8 py-6 h-auto rounded-none font-semibold tracking-wide group" data-testid="hero-book-btn">
              Book a Journey
              <ArrowRight className="ml-3 h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Button>
          </Link>
          <Link to="/fleet">
            <Button size="lg" variant="outline" className="border border-white/40 bg-transparent text-white hover:bg-white hover:text-black text-base px-8 py-6 h-auto rounded-none font-semibold tracking-wide" data-testid="hero-fleet-btn">
              View the Fleet
            </Button>
          </Link>
        </motion.div>
        <motion.div className="mt-14 hidden md:flex items-center gap-10 text-xs uppercase tracking-[0.2em] text-white/50" {...fade(base + 0.95)}>
          <span>24 / 7</span>
          <span className="h-px w-6 bg-white/30" />
          <span>UK-Wide Coverage</span>
          <span className="h-px w-6 bg-white/30" />
          <span>Discreet</span>
        </motion.div>
      </motion.div>

      <motion.div className="hidden lg:flex absolute right-10 bottom-12 z-10 flex-col items-center gap-3" {...fade(base + 1.1)}>
        <span className="text-[10px] tracking-[0.4em] uppercase text-white/50 [writing-mode:vertical-rl] rotate-180">Scroll</span>
        <motion.div className="h-10 w-px bg-white/40" animate={{ scaleY: [1, 0.4, 1] }} transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }} style={{ transformOrigin: 'top' }} />
      </motion.div>
    </section>
  );
};
